import React from 'react';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/material';
import { models, type Embed, type IReportEmbedConfiguration } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';

import { executeApi } from './Utils';
import useUserData from './hooks/useUserData';

interface PowerBIEmbedComponentProps {
	reportId: string;
	datasetId: string;
	filters?: models.ReportLevelFilters[];
}

const Root = styled('div')(({ theme }) => ({
	width: '100%',
	height: '100%',
	'& > .report-style-class': {
		height: '100%',
		borderRadius: 8
	}
}));

export default function PowerBIEmbedComponent({ reportId, datasetId, filters }: PowerBIEmbedComponentProps) {
	const dispatch = useDispatch();
	const userData = useUserData();

	const [report, setReport] = React.useState<Embed | undefined>();

	const [reportConfig, setReportConfig] = React.useState<IReportEmbedConfiguration>({
		type: 'report',
		tokenType: models.TokenType.Embed,
		filters,
		settings: {
			layoutType: models.LayoutType.Custom,
			customLayout: {
				displayOption: models.DisplayOption.FitToPage
			},
			localeSettings: { language: userData.user_language }
		}
	});

	const eventHandlersMap = new Map([
		['loaded', () => console.log('Report has loaded')],
		['rendered', () => console.log('Report has rendered')],
		[
			'error',
			event => {
				if (event) {
					if (event.detail.errorCode === '401' || event.detail.errorCode === '403') {
						getEmbedToken();
						report?.reload();
					}
				}
			}
		]
	]);

	const getEmbedToken = React.useCallback(() => {
		executeApi.get({
			url: `api/microsoft365/powerbi/report/token?dataset=${datasetId}&report=${reportId}`,
			dispatch,
			successHandler: ({ data }) => {
				setReportConfig(state => ({
					...state,
					embedUrl: `https://app.powerbi.com/reportEmbed?reportId=${reportId}`,
					accessToken: data.token
				}));
			}
		});
	}, [datasetId, dispatch, reportId]);

	React.useEffect(() => {
		getEmbedToken();
	}, [getEmbedToken]);

	return (
		<Root>
			<PowerBIEmbed
				cssClassName="report-style-class"
				embedConfig={reportConfig}
				eventHandlers={eventHandlersMap}
				getEmbeddedComponent={embedObject => setReport(embedObject)}
			/>
		</Root>
	);
}
